import React from 'react'
import { FormattedMessage, defineMessages, intlShape } from 'react-intl'
import PropTypes from 'prop-types'

// Components
import { Text, Page, ExternalLinkButton } from 'src/Components'
import { OverlayQuestionsFooter } from 'src/Components/shared/Routing'

// Constants
import {
  NightlyVRBOClick,
  NightlyAirbnbClick,
} from 'src/constants/trackingEvents'
import {
  HOMEOWNER_SHORT_TERM_RENTALS_TOPIC_KEY,
  BUYER_SHORT_TERM_RENTALS_TOPIC_KEY,
} from 'src/constants/topicKeys'
import { QUESTIONS } from 'src/constants/messages'

// Selectors
import { selectRouteKey } from 'src/store/selectors/routes'

// Utils
import { useFormatMessage } from 'src/providers/intl'
import airDnaLogo from './AirDNA-No-Padding.svg'
import airDnaLearnMoreImage from './AirDNA-LearnMore.png'

// Styles
import styles from './AirDnaLearnMorePanel.module.scss'

const propTypes = {
  intl: intlShape,
  withCurrentHomeData: PropTypes.bool,
}

const questionMessages = defineMessages({
  startToRent: {
    id: 'Questions.airdna.startToRent',
    defaultMessage: 'Can you help me get started?',
  },
  optimizeRent: {
    id: 'Questions.airdna.optimizeRent',
    defaultMessage: 'How else can short term rental income help me?',
  },
})

const messages = defineMessages({
  imageAlt: {
    id: 'Questions.airdna.imageAlt',
    defaultMessage: 'Your rental potential',
  },
})

const AirDnaLearnMorePanel = () => {
  const intlFormat = useFormatMessage()
  const isBuyer = selectRouteKey() === 'buy'
  const topicKey = isBuyer
    ? BUYER_SHORT_TERM_RENTALS_TOPIC_KEY
    : HOMEOWNER_SHORT_TERM_RENTALS_TOPIC_KEY

  return (
    <Page>
      <Page.Body>
        <img
          className={styles.image}
          src={airDnaLearnMoreImage}
          alt={intlFormat(messages.imageAlt)}
        />
        <Text
          size='xl'
          className={styles.headline}
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='AirDna.additionalInfo.headline'
            defaultMessage='Rentals are a great way to make extra income'
          />
        </Text>
        <Text size='s' variant='neutralCool' variantCount='700' align='left'>
          <FormattedMessage
            id='AirDna.additionalInfo.subheadline'
            defaultMessage='Sites like {airbnb} and {vrbo} make it really easy to rent out a room or your entire house on a nightly basis to people staying in town. It‘s great income and can be a fun way to help people get to know your city.'
            values={{ airbnb: AirbnbLink, vrbo: VrboLink }}
          />
        </Text>
        <Text
          size='xl'
          className={styles.subHeadline}
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='AirDna.additionalInfo.headlineTwo'
            defaultMessage='Make sure you‘re allowed to rent your home'
          />
        </Text>
        <Text
          size='s'
          paragraph
          variant='neutralCool'
          variantCount='700'
          align='left'
        >
          <FormattedMessage
            id='AirDna.additionalInfo.descriptionParaOne'
            defaultMessage='Some cities have laws that restrict your ability to host paying guests for short periods. These laws are often part of a city‘s zoning or administrative codes. In many cities, you must register, get a permit, or obtain a license before you list your property or accept guests. Also check to make sure your loan terms allow for short term rental.'
          />
        </Text>
        <Text size='s' variant='neutralCool' variantCount='700' align='left'>
          <FormattedMessage
            id='AirDna.additionalInfo.descriptionParaTwo'
            defaultMessage='These rules can be confusing. It‘s smart to work with your real estate agent to clarify these rules so that you have a clear understanding of what the laws are.'
          />
        </Text>
        <a
          href='https://www.airdna.co/'
          target='_blank'
          rel='noopener noreferrer'
          className={styles.attribution}
        >
          <div>
            <FormattedMessage
              id='AirDNA.attribution'
              defaultMessage='Powered by'
            />
          </div>
          <div>
            <img className={styles.imgAirdna} src={airDnaLogo} alt='airdna' />
          </div>
        </a>
      </Page.Body>

      <Page.Footer>
        <OverlayQuestionsFooter
          topic={intlFormat(QUESTIONS.airDnaTopic)}
          topicKey={topicKey}
          disableCospo
          questions={Object.values(questionMessages)}
          placeholder
        />
      </Page.Footer>
    </Page>
  )
}

const AirbnbLink = (
  <ExternalLinkButton
    minimal
    href='https://www.airbnb.com'
    track={NightlyAirbnbClick}
  >
    Airbnb
  </ExternalLinkButton>
)

const VrboLink = (
  <ExternalLinkButton
    minimal
    href='http://www.vrbo.com'
    track={NightlyVRBOClick}
  >
    VRBO
  </ExternalLinkButton>
)

AirDnaLearnMorePanel.propTypes = propTypes

export default AirDnaLearnMorePanel
